import {Injectable} from '@angular/core';
import {UrlHandlingStrategy, UrlSegmentGroup, UrlTree} from '@angular/router';

import {lessonViewPageName} from '@constants/app.config';

import {sidePanelAvailableRoutes} from '@utils/route-utils';

const aiScoringPanels = ['ai-autoscoring', 'ai-progress'];

const aiScoringPanelParams = ['action', 'qPK'];

@Injectable()
export class AppUrlHandlingStrategy extends UrlHandlingStrategy {
  shouldProcessUrl(url: UrlTree): boolean {
    return true;
  }

  extract(url: UrlTree): UrlTree {
    // Enforce panel scope: remove panel outlet if not allowed
    if (this._hasPanelOutlet(url) && !this._isPanelAllowedForRoute(url)) {
      this._removePanelOutlet(url);
    }

    this._removeStaleAiScoringParams(url);

    return url;
  }

  merge(newUrlPart: UrlTree, rawUrl: UrlTree): UrlTree {
    return newUrlPart;
  }

  /** Drops the AI scoring params once the panel that owns them is no longer open */
  private _removeStaleAiScoringParams(urlTree: UrlTree): void {
    const pageName = this._getPrimaryRouteSegments(urlTree)[0]?.path ?? '';
    if (!sidePanelAvailableRoutes.includes(pageName)) {
      return;
    }

    if (aiScoringPanels.includes(this._getPanelName(urlTree))) {
      return;
    }

    aiScoringPanelParams.forEach((param) => delete urlTree.queryParams[param]);
  }

  private _getPanelName(urlTree: UrlTree): string {
    return urlTree.root.children['panel']?.segments?.[0]?.path ?? '';
  }

  /** Checks if the URL tree contains a panel outlet */
  private _hasPanelOutlet(urlTree: UrlTree): boolean {
    return 'panel' in urlTree.root.children;
  }

  /** Checks if the panel outlet is allowed for the current primary route */
  private _isPanelAllowedForRoute(urlTree: UrlTree): boolean {
    const primarySegments = this._getPrimaryRouteSegments(urlTree);

    const pageName = primarySegments[0].path;

    if (pageName === lessonViewPageName) {
      const path = urlTree.root.children['panel'].toString();
      // used in the card details within the slide view
      if (['open', 'closed'].includes(path)) {
        return false;
      }

      if (primarySegments[2]?.path === 'print') {
        // print view
        return false;
      }
    }

    return (
      primarySegments.length >= 2 &&
      sidePanelAvailableRoutes.includes(pageName) &&
      !!primarySegments[1].path
    );
  }

  /** Gets the route segments from the primary outlet */
  private _getPrimaryRouteSegments(urlTree: UrlTree) {
    const primary = urlTree.root.children['primary'] as
      | UrlSegmentGroup
      | undefined;
    return primary?.segments ?? [];
  }

  /** Removes the panel outlet from the URL tree */
  private _removePanelOutlet(urlTree: UrlTree): void {
    delete urlTree.root.children['panel'];
  }
}
